import { getAccessToken } from '@app/helpers/localStorage';

import { destroyNativeLifecycleBridge } from './native-lifecycle-bridge';
import TopicManager from './topic-manager';
import WebSocketConnection from './websocket-connection';

let websocketConnectionSingleton: WebSocketConnection | null = null;
let topicManagerSingleton: TopicManager | null = null;

export function getWebSocketConnection(): WebSocketConnection {
  // TODO: are we mutating the process.env object?
  const { endpoint } = process.env.WEBSOCKET_ENDPOINT as unknown as {
    endpoint: string;
  };
  if (!endpoint) {
    throw new Error('WEBSOCKET_ENDPOINT is not set');
  }

  if (!websocketConnectionSingleton) {
    websocketConnectionSingleton = new WebSocketConnection({
      serverUrl: endpoint,
      fetchToken: getAccessToken,
    });
  }
  return websocketConnectionSingleton;
}

export function getTopicManager() {
  if (!topicManagerSingleton) {
    topicManagerSingleton = new TopicManager(getWebSocketConnection());
  }
  return topicManagerSingleton;
}

/**
 * Clean up WebSocket singletons. This should be called during logout or app shutdown.
 */
export function cleanupWebSocketSingletons() {
  destroyNativeLifecycleBridge();

  if (topicManagerSingleton) {
    topicManagerSingleton.cleanup();
    topicManagerSingleton = null;
  }

  if (websocketConnectionSingleton) {
    websocketConnectionSingleton.disconnect();
    websocketConnectionSingleton = null;
  }
}

/**
 * Get the current WebSocket connection status
 */
export function getWebSocketStatus() {
  if (!websocketConnectionSingleton) {
    return { connected: false, readyState: null };
  }

  const status = websocketConnectionSingleton.getConnectionStatus();

  return {
    ...status,
    lastTimestamp: websocketConnectionSingleton.lastTimestamp,
  };
}

/**
 * Manually disconnect the WebSocket connection
 */
export function disconnectWebSocket() {
  if (websocketConnectionSingleton) {
    websocketConnectionSingleton.disconnect();
  } else {
    console.log('[WEBSOCKET] No active connection to disconnect');
  }
}

export interface ReconnectWebSocketOptions {
  /** When true, close any existing socket (OPEN/CONNECTING) and create a new one. Use after app foreground so a silently broken mobile WebView socket is replaced. */
  force?: boolean;
}

/**
 * Internal helper: ensure a connection exists (create or reconnect), then return singletons.
 * Centralizes try/catch and error logging for reconnect vs create-new paths.
 */
async function ensureConnectionAndGetSingletons(force?: boolean): Promise<{
  connection: WebSocketConnection;
  topicManager: TopicManager | null;
}> {
  const hadSingleton = !!websocketConnectionSingleton;

  try {
    if (websocketConnectionSingleton) {
      websocketConnectionSingleton.shouldReconnect = true;
      await websocketConnectionSingleton.connect({ force });
      return {
        connection: websocketConnectionSingleton,
        topicManager: topicManagerSingleton,
      };
    }

    console.log(
      '[WEBSOCKET] No existing connection to reconnect, creating new one...',
    );
    const connection = getWebSocketConnection();
    const topicManager = getTopicManager();
    await connection.connect();
    console.log('[WEBSOCKET] New connection created and connected');
    return { connection, topicManager };
  } catch (error) {
    console.error(
      hadSingleton
        ? '[WEBSOCKET] Failed to reconnect:'
        : '[WEBSOCKET] Failed to create new connection:',
      error,
    );
    throw error;
  }
}

/**
 * Manually reconnect the WebSocket connection
 */
export async function reconnectWebSocket(options?: ReconnectWebSocketOptions) {
  return ensureConnectionAndGetSingletons(options?.force);
}

// Expose utility functions on window for browser console access (development only).
// Not exposed in production or test to avoid leaking internal operations (e.g. cleanup) via the console.
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  interface WebSocketUtilsInterface {
    disconnect: typeof disconnectWebSocket;
    reconnect: typeof reconnectWebSocket;
    status: typeof getWebSocketStatus;
    cleanup: typeof cleanupWebSocketSingletons;
  }

  // Extend the Window interface to include our utilities
  (
    window as unknown as Window & { WebSocketUtils: WebSocketUtilsInterface }
  ).WebSocketUtils = {
    disconnect: disconnectWebSocket,
    reconnect: reconnectWebSocket,
    status: getWebSocketStatus,
    cleanup: cleanupWebSocketSingletons,
  };
}
